/**
 * ./exercise4/Exercise4Workout.js
 *
 * This module **guides users through Exercise 4**, which focuses on **simultaneous image & text comprehension**
 * while maintaining **rhythmic breathing**. The exercise includes:
 * - **Randomly fetched images from Pixabay** 
 * - **Dynamically displayed sentences for interpretation**
 * - **Timed updates for images & sentences**
 * - **Live session tracking & progress monitoring**
 * - **Auto-completion & navigation to summary**
 *
 */


import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import './Exercise4Workout.css';
import CloseIcon from '../components/icons/close.svg';
import InfoIcon from '../components/icons/InfoIcon.svg'; 
import Curtain from '../components/Curtain';
import placeholderImage from './pexels-studio-naae-15863863-19121886.jpg';

import stick1 from "../components/icons/stick1.svg";
import stick2 from "../components/icons/stick2.svg";
import stick3 from "../components/icons/stick3.svg";
import stick4 from "../components/icons/stick4.svg";
import stick5 from "../components/icons/stick5.svg";
import stick6 from "../components/icons/stick6.svg";
import stick7 from "../components/icons/stick7.svg";
import { DEBUG_TIME } from "../config";

const PIXABAY_API_KEY = '46688585-53112bf837042b08ecb18f57f'; // Replace with your actual Pixabay API key
const PIXABAY_API_URL = 'https://pixabay.com/api/';

const CACHE_SIZE = 20; //cache, more fluent
const IMAGE_DISPLAY_DURATION = 5000; //milliseconds
const FETCH_THRESHOLD = 2; // Fetch new images when unused images are less than this number

function generateRandomSeed() {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < 10; i++) {
    result += characters.charAt(Math.floor(Math.random() * characters.length));
  }
  return result;
}

function Exercise4Workout() {
  const navigate = useNavigate();
  const location = useLocation(); // Add this to get the state passed from intro
  // Get duration from location state, default to 180 if not provided
  const initialDuration = location.state?.duration || 3; // Duration in minutes
  const [time, setTime] = useState(location.state?.time ?? initialDuration * 60); // Convert to seconds here
  const [progress, setProgress] = useState(0);
  const [showCurtain, setShowCurtain] = useState(false);
  const [currentImage, setCurrentImage] = useState(placeholderImage);
  const [currentSentence, setCurrentSentence] = useState('');
  const [isFirstImageFetched, setIsFirstImageFetched] = useState(false);
  const [isNextImageReady, setIsNextImageReady] = useState(false);
  const [imageCache, setImageCache] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(-1); // Start at -1 for placeholder
  const [logMessages, setLogMessages] = useState([]);
  const logContainerRef = useRef(null);
  const [usedImages, setUsedImages] = useState(new Set());
  const [sessionId, setSessionId] = useState(Date.now());
  const [randomSeed, setRandomSeed] = useState(generateRandomSeed());
  const [isPressed, setIsPressed] = useState(false);
  const [errorRate, setErrorRate] = useState(0);

  // breathing bar animation
  const [isPlaying, setIsPlaying] = useState(false);
  const [long, setLong] = useState(3.0);
  const [short, setShort] = useState(2.0);
  const [hold, setHold] = useState(1.0);
  const [currentStep, setCurrentStep] = useState(1);
  const [nextStep, setNextStep] = useState(2);
  const [previousStep, setPreviousStep] = useState(null);
  const [fadeInStyle, setFadeInStyle] = useState({ opacity: 1 });
  const [fadeOutStyle, setFadeOutStyle] = useState({ opacity: 0 });
  const [isTransitioningToOne, setIsTransitioningToOne] = useState(false);
  const [instructionText, setInstructionText] = useState('Play');

  const addLogMessage = (message) => {
    setLogMessages(prevMessages => [...prevMessages, `${new Date().toLocaleTimeString()}: ${message}`]);
  };

  // useEffect(() => {
  //   if (logContainerRef.current) {
  //     logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
  //   }
  // }, [logMessages]);

  // breathing animation 
  useEffect(() => {
    // Load values from localStorage
    setLong(parseFloat(localStorage.getItem('long')) || 2.0);
    setShort(parseFloat(localStorage.getItem('short')) || 1.0);
    setHold(parseFloat(localStorage.getItem('hold')) || 1.0);
  }, []);

  useEffect(() => {
    let interval;
    if (time > 0) {
      interval = setInterval(() => {
        setTime(prevTime => {
          const newTime = prevTime - 0.1;
          // Calculate progress based on initialDuration instead of hardcoded 180
          setProgress(100 - (newTime / (initialDuration * 60)) * 100);
          return newTime > 0 ? newTime : 0;
        });
      }, 100); // Update every 0.1 seconds
    } else {
      setShowCurtain(true);
      setTimeout(() => {
        navigate('/exercise4finish', {
          state: {
            duration: initialDuration,
            errorRate: errorRate
          }
        });
      }, 2000);
    }
    return () => clearInterval(interval);
  }, [time, navigate, initialDuration]); // Add initialDuration to dependencies
  const fetchRandomImages = async () => {
    try {
      addLogMessage('Initiating Pixabay API request for random images');
      
      // First, get the total number of pages
      const initialResponse = await axios.get(PIXABAY_API_URL, {
        params: {
          key: PIXABAY_API_KEY,
          orientation: 'horizontal',
          per_page: 50,
          safesearch: true,
          image_type: 'photo',
        },
      });

      const totalHits = initialResponse.data.totalHits;
      const totalPages = Math.ceil(totalHits / 200); // 200 is the max per_page allowed by Pixabay
      const randomPage = Math.floor(Math.random() * totalPages) + 1;

      addLogMessage(`Total pages: ${totalPages}, Selected page: ${randomPage}`);

      // Now fetch images from the randomly selected page
      const response = await axios.get(PIXABAY_API_URL, {
        params: {
          key: PIXABAY_API_KEY,
          orientation: 'horizontal',
          per_page: 200,
          page: randomPage,
          safesearch: true,
          image_type: 'photo',
          order: 'latest',
        },
      });
      
      if (response.data.hits.length > 0) {
        const allImages = response.data.hits.map(photo => photo.webformatURL);
        // Randomly select 20 images from the fetched images
        const randomImages = [];
        const selectedIndices = [];
        for (let i = 0; i < 20 && allImages.length > 0; i++) {
          const randomIndex = Math.floor(Math.random() * allImages.length);
          randomImages.push(allImages[randomIndex]);
          selectedIndices.push(randomIndex);
          allImages.splice(randomIndex, 1);
        }
        addLogMessage(`Fetched ${randomImages.length} random images from Pixabay (page ${randomPage} of ${totalPages})`);
        addLogMessage(`Selected image indices: ${selectedIndices.join(', ')}`);
        setRandomSeed(generateRandomSeed()); // Generate a new seed for the next request
        return randomImages;
      } else {
        addLogMessage('No images found in the Pixabay API response');
        return [];
      }
    } catch (error) {
      let errorMessage = 'Error fetching images from Pixabay';
      if (error.response) {
        errorMessage += `: ${error.response.status} - ${error.response.data}`;
        addLogMessage(`Pixabay API error: ${error.response.status} ${error.response.statusText}`);
        addLogMessage(`Error details: ${JSON.stringify(error.response.data)}`);
        
        if (error.response.status === 429) {
          addLogMessage('Rate limit exceeded. Please wait before making more requests.');
        } else if (error.response.status === 401) {
          addLogMessage('Unauthorized. Check if your API key is valid and not expired.');
        } else if (error.response.status >= 500) {
          addLogMessage('Pixabay API server error. The service might be experiencing issues.');
        }
      } else if (error.request) {
        errorMessage += ': No response received from Pixabay API';
        addLogMessage('No response received from Pixabay API');
        
        if (error.code === 'ECONNABORTED') {
          addLogMessage('Request timed out. Check your internet connection or try again later.');
        } else if (error.code === 'ENOTFOUND') {
          addLogMessage('Network error. Unable to reach Pixabay API. Check your internet connection.');
        } else {
          addLogMessage(`Network error: ${error.code}. The Pixabay API might be down or unreachable.`);
        }
      } else {
        errorMessage += `: ${error.message}`;
        addLogMessage(`Error setting up Pixabay API request: ${error.message}`);
      }
      console.error(errorMessage);
      addLogMessage(errorMessage);
    }
    return [];
  };

  const updateImageCache = async () => {
    const unusedImagesCount = imageCache.length - (currentImageIndex + 1);
    addLogMessage(`Unused images in cache: ${unusedImagesCount}`);
    
    if (unusedImagesCount >= FETCH_THRESHOLD) {
      addLogMessage('Sufficient images in cache, no update needed');
      return;
    }
    
    addLogMessage('Fetching new batch of images');
    const newImages = await fetchRandomImages();
    
    setImageCache(prevCache => {
      const updatedCache = [...prevCache.slice(currentImageIndex + 1), ...newImages];
      const filteredCache = updatedCache.filter(img => !usedImages.has(img));
      const finalCache = filteredCache.slice(0, CACHE_SIZE);
      addLogMessage(`Updated cache size: ${finalCache.length}`);
      return finalCache;
    });
  };

  useEffect(() => {
    const initializeCache = async () => {
      addLogMessage('Initializing cache');
      await updateImageCache();
    };
    initializeCache();
  }, []);

  useEffect(() => {
    if (imageCache.length > 0 && !isFirstImageFetched) {
      addLogMessage('First image fetched, waiting to display');
      setIsFirstImageFetched(true);
    }
  }, [imageCache, isFirstImageFetched]);

  useEffect(() => {
    // Fetch a sentence for the placeholder image
    fetchRandomSentence();
  }, []); // This will run once when the component mounts

  const fetchRandomSentence = () => {
    // Replace this with an actual API call or a more sophisticated method to generate sentences
    const sentences = [
"The quick brown fox jumps over a lazy dog.",
    "How vexingly quick daft zebras jump!",
    "Sphinx of black quartz, judge my vow.",
    "Pack my box with five dozen liquor jugs.",
    "The five boxing wizards jump quickly.",
    "The quick brown fox jumps over the lazy dog.",
    "A journey of a thousand miles begins with a single step.",
    "To be or not to be, that is the question.",
    "All that glitters is not gold.",
    "Where there's a will, there's a way.",
    "The early bird catches the worm.",
    "Actions speak louder than words.",
    "Knowledge is power, but enthusiasm pulls the switch.",
    "Life is what happens when you're busy making other plans.",
    "The only way to do great work is to love what you do.",
    "She made a delicious cake for her sister's birthday.",
    "The old car broke down on the way home.",
    "My brother plays soccer every Sunday in the park.",
    "The students studied hard for their final math exam.",
    "Birds are singing in the trees near my window.",
    "He painted the walls of his room bright blue.",
    "The children built a sandcastle on the sunny beach.",
    "Our teacher gave us homework to do over weekend.",
    "The cat chased a mouse through the empty kitchen.",
    "They went camping in the forest last summer vacation.",
    "The garden looks beautiful after the spring rain.",
    "I found my lost keys under the couch cushion.",
    "She reads a new book every week at night.",
    "The dog wagged its tail when we came home.",
    "We had pizza and ice cream for dinner today.",
    "The movie was so funny that everyone kept laughing.",
    "He rides his bicycle to work every morning.",
    "The baby smiled when her mother sang a lullaby.",
    "They planted new flowers in the backyard garden.",
    "My sister practices piano for two hours each day.",
    "The bus was late because of heavy traffic.",
    "We watched the sunset from our front porch.",
    "The chef prepared a special meal for the guests.",
    "She writes stories about adventures in magical lands.",
    "The team won their first game of the season.",
    "He fixed the broken chair with some new tools.",
    "The rain stopped just before our picnic began.",
    "They painted beautiful pictures during art class today.",
    "My grandmother bakes cookies every Sunday afternoon.",
    "The library has many new books this month.",
    "We saw dolphins jumping in the ocean waves.",
    "The farmer collected fresh eggs from the chickens.",
    "She helped her friend move into a new house.",
    "The children played hide and seek in the park.",
    "He learned to play guitar from online lessons.",
    "The store had a big sale on winter clothes.",
    "They built a snowman after the first snowfall.",
    "My dad grilled hamburgers for our family dinner.",
    "The teacher explained the math problem very clearly.",
    "We picked apples from trees in the orchard.",
    "The puppy chased its tail around the yard.",
    "She bought fresh vegetables from the local market.",
    "The boys played basketball in the school gym.",
    "Our neighbors invited us for dinner last night.",
    "He found a four-leaf clover in the grass.",
    "The wind blew leaves all around the yard.",
    "They decorated the house for the holiday party.",
    "My mom makes the best pancakes for breakfast.",
    "The train arrived five minutes ahead of schedule.",
    "We went swimming at the community pool today.",
    "The bird built its nest in our tree.",
    "She finished her homework before watching television.",
    "The dentist cleaned my teeth very gently.",
    "They walked their dog around the neighborhood.",
    "He scored the winning goal in the game.",
    "The cake tasted better than it looked.",
    "We visited our grandparents during summer vacation.",
    "The squirrel collected nuts for the winter season.",
    "She drew a picture of her family together.",
    "The mail carrier delivered packages to our door.",
    "They sang happy birthday at the surprise party.",
    "My friend taught me how to ride skateboard.",
    "The fish swam quickly in the clear pond.",
    "He washed the dishes after eating dinner.",
    "The clock struck midnight as the party ended.",
    "We planted tomatoes in our vegetable garden.",
    "The butterfly landed on a colorful flower.",
    "She organized her closet on Saturday morning.",
    "The firefighters quickly put out the fire.",
    "They built a tree house in the backyard.",
    "My cousin plays drums in a rock band.",
    "The doctor gave me medicine for my cold.",
    "We made sandwiches for the school lunch.",
    "The rabbit hopped through the garden quickly.",
    "He collected stamps from different countries.",
    "The waves crashed loudly on the beach shore.",
    "They rode horses at the summer camp.",
    "My aunt baked a chocolate cake for dessert.",
    "The painter created beautiful art on canvas.",
    "She solved the puzzle in record time.",
    "The janitor cleaned the floors after school.",
    "We watched fireworks on New Year's Eve.",
    "The kitten played with a ball of yarn.",
    "He wrote a letter to his pen pal.",
    "The leaves changed colors in the fall.",
    "They played video games after finishing homework.",
    "My sister braided her hair this morning.",
    "The mechanic fixed our car's broken engine.",
    "We built a fort using blankets and chairs.",
    "The bee collected honey from the flowers.",
    "She watered the plants in her garden.",
    "The police officer helped the lost child.",
    "They made hot chocolate on the cold day.",
    "My brother caught a fish in the lake.",
    "The baker made fresh bread every morning.",
    "We saw stars through the new telescope.",
    "The elephant sprayed water with its trunk.",
    "He cleaned his room before going outside.",
    "The snow fell softly during the night.",
    "They played board games on rainy days.",
    "My teacher read us a story after lunch.",
    "The photographer took pictures at the wedding.",
    "We rode our bikes through the park.",
    "The monkey swung from tree to tree.",
    "She packed her lunch for school tomorrow.",
    "The plumber fixed the leaking sink quickly.",
    "They watched the parade from the sidewalk.",
    "My uncle drives a truck for work.",
    "The artist painted a picture of mountains.",
    "We made popcorn for movie night.",
    "The lion roared loudly at the zoo.",
    "He practiced typing on the computer keyboard.",
    "The wind chimes made music in the breeze.",
    "They collected seashells along the beach.",
    "My friend won first prize in the contest.",
    "The dentist filled the cavity in my tooth.",
    "We climbed the hill to watch sunset.",
    "The parrot repeated words it heard today.",
    "She jumped rope during recess at school.",
    "The chef cooked pasta for the restaurant.",
    "They played catch in the front yard.",
    "My parents bought a new car yesterday.",
    "The musician played guitar at the concert.",
    "We fed ducks at the local pond.",
    "The turtle slowly crossed the sandy beach.",
    "He built a model airplane from scratch.",
    "The thunder boomed during the storm.",
    "They made friendship bracelets at summer camp.",
    "My neighbor grows beautiful roses in spring.",
    "The barber cut hair at the shop.",
    "We watched the planes take off.",
    "The penguin waddled across the ice.",
    "She collected rocks from the hiking trail.",
    "The carpenter made a wooden table.",
    "They played soccer in the rain.",
    "My dog buried his bone in garden.",
    "The nurse helped patients at the hospital.",
    "We rode the roller coaster three times.",
    "The deer ran through the forest quickly.",
    "He drew cartoons in his sketchbook.",
    "The rainbow appeared after the rain stopped.",
    "They made sandcastles on the beach.",
    "My sister plays volleyball after school.",
    "The pilot flew the plane through clouds.",
    "We bought groceries at the supermarket.",
    "The frog jumped into the pond.",
    "She planted flowers in the window box.",
    "The teacher wrote equations on the board.",
    "They went fishing early this morning.",
    "My cousin lives in a different city.",
    "The dancer performed on the stage.",
    "We saw bears at the national park.",
    "The spider spun a web overnight.",
    "He collected baseball cards as a hobby.",
    "The wind blew the kite high.",
    "They made cookies for the bake sale.",
    "My mom teaches math at school.",
    "The lifeguard watched swimmers at the pool.",
    "We built snowmen after the blizzard.",
    "The owl hooted in the night.",
    "She played hopscotch with her friends.",
    "The farmer drove his tractor through fields.",
    "They ate ice cream on hot day.",
    "My dad fixed the broken fence today.",
    "The magician performed tricks at the party.",
    "We picked strawberries at the farm.",
    "The squirrel climbed up the tall tree.",
    "He practiced piano every afternoon.",
    "The leaves rustled in the wind.",
    "They played hide and seek inside.",
    "My friend drew pictures during class.",
    "The cook prepared lunch for students.",
    "We watched the meteor shower last night.",
    "The horse galloped across the field.",
    "She solved math problems after dinner.",
    "The electrician fixed the broken light.",
    "They rode bikes around the neighborhood.",
    "My brother built a robot from parts.",
    "The singer performed at the school concert.",
    "We planted trees on Earth Day.",
    "The fox ran through the garden.",
    "He made paper airplanes during recess.",
    "The waves splashed against the rocks.",
    "They played chess in the library.",
    "My aunt teaches art at school.",
    "The vet helped sick animals get better.",
    "We went camping by the lake.",
    "The bear caught fish in the stream.",
    "She wrote stories in her journal.",
    "The bus driver drove carefully in rain.",
    "They collected leaves for science class.",
    "My grandpa tells interesting stories about history.",
    "The actor performed in the school play.",
    "We saw shooting stars last night.",
    "The rabbit ate carrots from the garden.",
    "He played drums in the school band.",
    "The snow covered everything in white.",
    "They made a movie about their vacation.",
    "My sister sang in the choir.",
    "The painter decorated walls with bright colors.",
    "We found shells on the beach.",
    "The duck swam in the pond.",
    "She helped clean the classroom after school.",
    "The gardener planted new trees today.",
    "They watched birds build their nests.",
    "My friend learned to surf this summer.",
    "The dentist checked my teeth carefully.",
    "We played games at the birthday party.",
    "The cat napped in the sunny window.",
    "He wrote a story about space.",
    "The rain made puddles in yard.",
    "They built towers with wooden blocks.",
    "My teacher plays guitar during music class.",
    "The chef baked bread in kitchen.",
    "We saw dolphins swimming near shore.",
    "The dog chased its tail around.",
    "She drew pictures with colored chalk.",
    "The mailman delivered letters every morning.",
    "They played tag during recess time.",
    "My mom planted flowers in garden.",
    "The scientist conducted experiments in lab.",
    "We rode horses at the farm.",
    "The bird sang from the tree branch.",
    "He solved the puzzle very quickly.",
    "The wind moved clouds across sky.",
    "They made paper boats to float.",
    "My dad grilled burgers for dinner.",
    "The librarian read stories to children.",
    "We saw fireworks on Fourth of July.",
    "The fish jumped out of water.",
    "She played piano at the recital.",
    "The carpenter built new shelves today.",
    "They walked to school every morning.",
    "My brother plays baseball after school.",
    "The artist drew pictures in park.",
    "We found treasures at the beach.",
    "The butterfly landed on my shoulder.",
    "He climbed trees in the backyard.",
    "The stars twinkled in night sky.",
    "They made snowballs during winter break.",
    "My friend rides horses every weekend.",
    "The doctor helped sick people feel better.",
    "We planted vegetables in our garden.",
    "The mouse ran under the cabinet.",
    "She sang songs at the concert.",
    "The teacher helped students learn math.",
    "They played music at the party.",
    "My sister swims in the pool.",
    "The baker made cookies for sale.",
    "We watched clouds change shapes.",
    "The deer ate grass in field.",
    "He built sandcastles at the beach.",
    "The rain helped flowers grow taller.",
    "They collected bugs for science class.",
    "My aunt makes delicious apple pies.",
    "The pilot flew through the clouds.",
    "We saw rainbows after the storm.",
    "The snake slithered through the grass.",
    "She played games with her friends.",
    "The farmer harvested corn from fields.",
    "They rode bikes to the park.",
    "My cousin plays soccer very well.",
    "The painter drew pictures of animals.",
    "We found rocks by the river.",
    "The bee buzzed around the flowers.",
    "He caught butterflies with his net.",
    "The moon shone brightly at night.",
    "They made crafts from recycled materials.",
    "My grandma bakes cookies every weekend.",
    "The firefighter saved the cat.",
    "We played basketball in the gym.",
    "The lizard basked in the sun.",
    "She wrote letters to her friends.",
    "The cook made soup for lunch.",
    "They built houses for the birds.",
    "My uncle takes pictures of nature.",
    "The nurse helped the injured player.",
    "We saw stars through the telescope.",
    "The grasshopper jumped through tall grass.",
    "He drew maps of treasure islands.",
    "The leaves fell from the trees.",
    "They played music on their guitars.",
    "My friend teaches others how to dance.",
    "The mechanic fixed the broken bicycle.",
    "We watched boats on the lake.",
    "The owl watched from the tree.",
    "She planted seeds in small pots.",
    "The driver stopped at red lights.",
    "They collected coins from different countries.",
    "My dad reads stories before bedtime.",
    "The swimmer dove into the pool.",
    "We made art with colored paper.",
    "The chipmunk gathered nuts for winter.",
    "He played catch with his dog.",
    "The sun set behind the mountains.",
    "They drew pictures with sidewalk chalk.",
    "Students rushed to their classrooms before the bell rang.",
    "Dogs bark loudly at passing cars on our street.",
    "Cats sleep peacefully in warm patches of sunlight.",
    "Birds build their nests high in maple trees.",
    "Teachers write important notes on the whiteboard daily.",
    "Doctors help sick patients feel better every day.",
    "Butterflies flutter gently from flower to flower all morning.",
    "Children laugh while playing games during summer vacation.",
    "Parents pack healthy lunches for their kids everyday.",
    "Flowers bloom beautifully in our garden during spring.",
    "Turn off all lights before leaving the room.",
    "Clean your room before going out to play.",
    "Write your homework assignments in your planner carefully.",
    "Draw a picture of your favorite animal today.",
    "Read this chapter before coming to class tomorrow.",
    "Wash your hands before every meal time.",
    "Listen carefully to instructions before starting the project.",
    "Pack all necessary items for the camping trip.",
    "Study these vocabulary words for tomorrow's test.",
    "Bring your textbook to every class meeting.",
    "Rabbits hop quickly through the vegetable garden patches.",
    "Musicians practice their instruments every single morning.",
    "Painters create beautiful artwork in their quiet studios.",
    "Bakers prepare fresh bread early each morning.",
    "Scientists conduct interesting experiments in their laboratories.",
    "Farmers harvest crops during sunny autumn days.",
    "Gardeners plant new flowers in springtime weather.",
    "Runners exercise in the park every morning.",
    "Swimmers practice their strokes in the pool.",
    "Chefs cook delicious meals in busy restaurants.",
    "Make your bed right after waking up.",
    "Finish all homework before watching any television.",
    "Remember to call grandma every Sunday afternoon.",
    "Practice piano for thirty minutes each day.",
    "Water the plants in your garden regularly.",
    "Feed your pets at the same time daily.",
    "Complete these math problems before going outside.",
    "Put away your toys after playing games.",
    "Organize your desk before starting new assignments.",
    "Check your answers before submitting the test.",
    "Squirrels gather acorns for their winter food supply.",
    "Dolphins jump playfully through ocean waves near shore.",
    "Bears catch fresh salmon from mountain streams.",
    "Deer graze peacefully in green meadow grass.",
    "Lions rest under shady trees during hot days.",
    "Monkeys swing gracefully between jungle tree branches.",
    "Penguins waddle across frozen Antarctic ice fields.",
    "Eagles soar high above mountain peaks searching.",
    "Wolves hunt together through snowy forest paths.",
    "Elephants spray cool water during hot afternoons.",
    "Start your homework right after school ends.",
    "Follow these directions to reach your destination.",
    "Keep your workspace clean and organized always.",
    "Save some money from your weekly allowance.",
    "Share your toys with younger siblings kindly.",
    "Help your parents with household chores daily.",
    "Learn one new thing every single day.",
    "Drink plenty of water throughout the day.",
    "Exercise for at least thirty minutes daily.",
    "Brush your teeth after every meal time.",
    "Raindrops fall gently on colorful autumn leaves.",
    "Snowflakes drift slowly from gray winter clouds.",
    "Sunshine warms the sandy beach each morning.",
    "Wind blows through tall grass in meadows.",
    "Waves crash against rocky coastal cliffs repeatedly.",
    "Lightning flashes brightly across dark storm skies.",
    "Thunder rumbles loudly during summer rain storms.",
    "Clouds move slowly across bright blue skies.",
    "Stars twinkle brightly in clear night skies.",
    "Rain falls softly on flower garden beds.",
    "Open your textbook to page fifty three.",
    "Write neatly when completing your assignments today.",
    "Raise your hand before asking any questions.",
    "Speak clearly when giving your presentation tomorrow.",
    "Walk quietly through hallways between classes please.",
    "Show respect to everyone in your class.",
    "Return library books before their due date.",
    "Submit your project before Friday afternoon.",
    "Follow safety rules during science experiments carefully.",
    "Complete all exercises in your workbook today.",
    "Airplanes fly high above fluffy white clouds.",
    "Trains travel quickly between busy city stations.",
    "Buses carry students safely to school daily.",
    "Cars stop carefully at red traffic lights.",
    "Bicycles move swiftly along neighborhood streets.",
    "Boats sail smoothly across calm lake waters.",
    "Trucks deliver fresh food to local stores.",
    "Motorcycles cruise down empty highway roads.",
    "Helicopters hover above tall city buildings.",
    "Submarines explore deep ocean waters silently.",
    "Remember important dates in your calendar carefully.",
    "Choose healthy snacks between main meals daily.",
    "Create beautiful artwork using colored pencils today.",
    "Build strong friendships with your classmates here.",
    "Solve these puzzles during free time today.",
    "Plant new seeds in your garden bed.",
    "Keep your notebook organized and clean always.",
    "Find interesting books at the library today.",
    "Learn new songs on your musical instrument.",
    "Draw detailed maps of your neighborhood area.",
    "Mountains rise majestically above valley floors below.",
    "Rivers flow swiftly through deep canyon walls.",
    "Forests grow dense with tall pine trees.",
    "Deserts stretch endlessly under hot sun rays.",
    "Oceans contain countless mysterious sea creatures.",
    "Valleys lie peacefully between mountain ranges.",
    "Lakes reflect surrounding trees like mirrors.",
    "Fields stretch endlessly with golden wheat crops.",
    "Beaches extend along blue ocean shores.",
    "Gardens bloom with countless colorful flowers.",
    "Measure ingredients carefully before starting to cook.",
    "Count money accurately before making any purchase.",
    "Sort laundry into different color piles first.",
    "Fold clean clothes neatly before putting away.",
    "Set the table properly before dinner time.",
    "Place books back on proper shelf locations.",
    "Arrange furniture neatly in your room.",
    "Collect all necessary materials before starting projects.",
    "Store food properly in clean containers always.",
    "Pack your backpack for tomorrow morning now.",
    "Autumn leaves fall gently from maple trees.",
    "Spring flowers bloom early in morning sunlight.",
    "Summer breezes blow through open windows softly.",
    "Winter snow covers ground with white blankets.",
    "Morning dew sparkles on green grass blades.",
    "Evening shadows grow longer across empty playgrounds.",
    "Afternoon sun warms sandy beach shores nicely.",
    "Night stars shine brightly in clear skies.",
    "Dawn breaks quietly over sleeping city streets.",
    "Dusk settles slowly over quiet neighborhood homes.",
    "Add these numbers carefully before writing answers.",
    "Print your name clearly on assignment papers.",
    "Cross streets safely at designated crosswalks only.",
    "Tie your shoelaces before running outside please.",
    "Close all windows before leaving your room.",
    "Lock doors securely when leaving home alone.",
    "Carry heavy books carefully in your backpack.",
    "Hold handrails while climbing steep stairs.",
    "Wait patiently for your turn in line.",
    "Ask questions politely when needing help.",
    "Soccer players kick balls across green fields.",
    "Basketball players shoot hoops during practice time.",
    "Baseball players catch fly balls in outfields.",
    "Tennis players serve balls over high nets.",
    "Football players run quickly down grassy fields.",
    "Volleyball players jump high above net lines.",
    "Hockey players skate fast on smooth ice.",
    "Swimmers dive gracefully into cool pool water.",
    "Gymnasts perform amazing flips through open air.",
    "Dancers move gracefully across wooden stage floors.",
    "Write stories about your summer vacation adventures.",
    "Draw pictures of your favorite animal friends.",
    "Sing songs during morning music class time.",
    "Read books quietly during library visiting time.",
    "Paint beautiful pictures using bright watercolors today.",
    "Build towers using wooden blocks carefully.",
    "Play games with friends during recess time.",
    "Make crafts using colored paper and glue.",
    "Solve puzzles during indoor break times.",
    "Create art projects using recycled materials now.",
    "Puppies play happily with soft rubber balls.",
    "Kittens chase red laser dots across floors.",
    "Hamsters run quickly in spinning exercise wheels.",
    "Parrots repeat funny words they hear daily.",
    "Goldfish swim circles in glass bowl homes.",
    "Rabbits nibble fresh carrots from gardens.",
    "Turtles bask warmly under heat lamps.",
    "Lizards catch insects with quick tongue flicks.",
    "Frogs jump between lily pads in ponds.",
    "Guinea pigs squeak excitedly at feeding times.",
    "Measure twice before cutting any construction materials.",
    "Double check answers before submitting math tests.",
    "Research topics thoroughly before writing papers.",
    "Practice instrument scales before playing new songs.",
    "Stretch muscles carefully before exercising outside.",
    "Gather materials needed before starting art projects.",
    "Plan routes carefully before starting long journeys.",
    "Save documents frequently while working on computers.",
    "Review notes carefully before taking important tests.",
    "Prepare ingredients before cooking new recipes carefully.",
    "Students write notes in their notebooks during science class.",
    "Dogs chase frisbees across the grassy park field.",
    "Leaves rustle gently in the autumn morning breeze.",
    "Mother birds feed worms to their hungry babies.",
    "Clean dishes thoroughly before putting them away.",
    "Organize your backpack before going to school tomorrow.",
    "Write each vocabulary word three times for practice.",
    "Brush teeth thoroughly after eating sweet snacks.",
    "Vacuum cleaners hum loudly across carpet floors.",
    "Pencils scratch quietly against paper during test time.",
    "Finish homework assignments before playing video games today.",
    "Place dirty clothes in the laundry basket.",
    "Water houseplants once every three days carefully.",
    "Check both ways before crossing busy streets.",
    "Grandmothers knit warm sweaters during winter evenings.",
    "Mailboxes stand quietly along neighborhood streets each morning.",
    "Alarm clocks ring loudly at seven each morning.",
    "Cookies bake slowly in the warm kitchen oven.",
    "Paint brushes swish through clear water between colors.",
    "Lunch boxes wait patiently in classroom cubbies.",
    "Remove shoes before walking on clean carpets.",
    "Answer phone calls with a polite greeting.",
    "Wash fruits thoroughly before eating them fresh.",
    "Study multiplication tables for fifteen minutes each day.",
    "Grass grows greener after spring rain showers.",
    "Computers hum softly in the quiet office space.",
    "Prepare ingredients before starting any cooking project.",
    "Hang wet towels properly after swimming lessons.",
    "Ladybugs crawl slowly across green garden leaves.",
    "Backpacks hang heavily from student shoulders.",
    "Save money carefully in your piggy bank.",
    "Practice musical scales before playing new songs.",
    "Measure flour twice for baking perfect cookies.",
    "Fold paper airplanes carefully along dotted lines.",
    "Sunflowers turn their faces toward bright sunlight.",
    "Bicycles lean against garage walls after rides.",
    "Collect fallen leaves for art project today.",
    "Arrange books alphabetically on library shelves.",
    "Snowflakes dance gracefully through winter air.",
    "Lunch trays clatter quietly in school cafeterias.",
    "Cover textbooks with protective paper carefully.",
    "Print handwriting practice sheets for homework tonight.",
    "Rabbits munch carrots from vegetable garden rows.",
    "Scissors cut carefully along marked paper lines.",
    "Doorbells ring cheerfully throughout neighborhood homes.",
    "Paint brushes create colorful art on blank pages.",
    "Calculators help solve difficult math problems quickly.",
    "Garden hoses spray water on thirsty plants.",
    "Violins produce beautiful music during orchestra practice.",
    "Finish math homework before watching television shows.",
    "Pack school lunches night before classes begin.",
    "Listen attentively during important class discussions.",
    "Raise hands before answering teacher questions.",
    "Sharpen pencils before starting writing assignments.",
    "Recycle paper products in blue bins only.",
    "Erase board completely after each lesson ends.",
    "Goldfish swim gracefully in clear glass bowls.",
    "Classroom chairs scrape quietly across floor tiles.",
    "Soccer balls roll swiftly across green fields.",
    "Lunch boxes contain healthy sandwiches and fruits.",
    "Paintbrushes create beautiful artwork on blank canvases.",
    "Pencil sharpeners whir loudly in quiet classrooms.",
    "Drinking fountains provide cool water between classes.",
    "Playground swings move back and forth smoothly.",
    "Library books wait patiently on wooden shelves.",
    "Classroom clocks tick steadily throughout school days.",
    "Complete reading assignments before bedtime tonight.",
    "Sort recycling materials into proper containers carefully.",
    "Follow recipe instructions exactly while baking cookies.",
    "Draw straight lines using rulers and pencils.",
    "Butterflies land delicately on bright garden flowers.",
    "Raindrops tap gently against window panes.",
    "Autumn winds scatter colorful leaves everywhere.",
    "Morning sunshine streams through classroom windows.",
    "Basketball hoops stand tall in empty gymnasiums.",
    "Lunch tables fill quickly during busy periods.",
    "Submit completed assignments before Friday afternoon deadline.",
    "Record homework assignments in planners every day.",
    "Close textbooks quietly after finishing reading assignments.",
    "Place sharp scissors in proper storage containers.",
    "Microscopes reveal tiny worlds in science labs.",
    "Drinking straws make bubbles in cold milk.",
    "Rubber bands stretch around thick paper stacks.",
    "Paper clips hold important documents together securely.",
    "Staples fasten papers together with sharp clicks.",
    "Glue sticks attach paper firmly to projects.",
    "Remember important dates in your daily planner.",
    "Collect necessary materials before starting projects.",
    "Divide large tasks into smaller manageable parts.",
    "Create detailed outlines before writing essays.",
    "Sandwich bags keep lunch foods fresh until noon.",
    "Juice boxes quench thirst during recess breaks.",
    "Apple trees provide shade in school yards.",
    "Crossing guards help students cross busy streets.",
    "School buses transport children safely each morning.",
    "Lunch monitors help maintain cafeteria order.",
    "Write thank you notes after receiving special gifts.",
    "Clean paintbrushes thoroughly after art class ends.",
    "Return borrowed items promptly to their owners.",
    "Empty trash bins before they become too full.",
    "Bumblebees buzz busily among garden flower beds.",
    "Maple trees drop helicopter seeds in spring.",
    "Pine needles carpet forest floors with softness.",
    "Ocean waves crash rhythmically against sandy shores.",
    "Mountain streams flow swiftly over smooth rocks.",
    "Desert cacti store water for dry seasons.",
    "Prairie grasses wave gently in summer breezes.",
    "Forest mushrooms grow quietly in damp shade.",
    "River otters play joyfully in rushing waters.",
    "Beach shells sparkle beautifully in morning light.",
    "Follow map directions carefully during nature hikes.",
    "Mark trail paths clearly for other hikers.",
    "Carry water bottles during long walking trips.",
    "Wear appropriate shoes for outdoor activities.",
    "Pack necessary supplies before camping trips begin.",
    "Check weather forecasts before planning outdoor activities.",
    "Apply sunscreen regularly during beach visits.",
    "Store camping gear properly after each use.",
    "Clean hiking boots after muddy trail walks.",
    "Fill bird feeders regularly during winter months.",
    "Garden tools rest quietly in storage sheds.",
    "Flower petals dance gently in spring breezes.",
    "Vegetable seeds sprout slowly in warm soil.",
    "Grass clippings mulch garden beds naturally.",
    "Tomato plants climb steadily up garden stakes.",
    "Potato plants hide treasures underground patiently.",
    "Carrot tops peek through garden soil rows.",
    "Pea vines climb gracefully up garden trellises.",
    "Strawberry plants spread runners through garden beds.",
    "Mint leaves release fragrance when touched gently.",
    "Place garden tools away after each use.",
    "Water seedlings gently with spray bottles.",
    "Remove weeds regularly from garden beds.",
    "Add compost to gardens every spring season.",
    "Plant flower bulbs before winter frost arrives.",
    "Harvest vegetables at their peak ripeness.",
    "Save seeds from successful garden plants.",
    "Mulch garden beds before winter weather arrives.",
    "Prune dead branches from healthy plants carefully.",
    "Label plant rows clearly in vegetable gardens.",
    "Musical instruments wait quietly in practice rooms.",
    "Piano keys shine brightly in morning sunlight.",
    "Drum sticks tap rhythmically on practice pads.",
    "Guitar strings vibrate with melodic sounds.",
    "Violin bows glide smoothly across strings.",
    "Flute notes float softly through practice halls.",
    "Trumpet valves click precisely during scale practice.",
    "Clarinet reeds produce warm musical tones.",
    "Saxophone notes jazz up quiet practice sessions.",
    "Trombone slides move smoothly during band practice.",
    "Practice musical pieces slowly at first.",
    "Count rhythm beats carefully while playing music.",
    "Tune instruments properly before each practice session.",
    "Follow conductor directions during orchestra rehearsals.",
    "Mark difficult passages in music sheets clearly.",
    "Clean instruments thoroughly after each use.",
    "Store sheet music carefully in proper folders.",
    "Replace broken strings promptly on instruments.",
    "Organize music sheets before rehearsals begin.",
    "Schedule regular practice times each day.",
    "Science experiments teach students about natural laws.",
    "Laboratory beakers hold colorful liquid solutions.",
    "Microscope slides reveal hidden microscopic worlds.",
    "Test tubes bubble with chemical reactions.",
    "Safety goggles protect eyes during experiments.",
    "Measuring cylinders ensure accurate liquid amounts.",
    "Bunsen burners heat solutions carefully controlled.",
    "Magnets attract metal objects across tables.",
    "Thermometers measure temperature changes accurately.",
    "Balance scales weigh materials precisely.",
    "Record experimental results in lab notebooks.",
    "Follow safety procedures during science experiments.",
    "Clean laboratory equipment after each use.",
    "Wear protective gear during chemical experiments.",
    "Label test tubes clearly before experiments begin.",
    "Store chemicals properly in designated cabinets.",
    "Dispose hazardous materials in proper containers.",
    "Check equipment condition before each experiment.",
    "Report safety concerns to teachers immediately.",
    "Keep laboratory spaces clean and organized.",
    "Art supplies create colorful masterpieces daily.",
    "Crayons draw bright pictures on white paper.",
    "Colored pencils shade drawings with precision.",
    "Marker pens make bold artistic statements.",
    "Watercolors blend beautifully on wet paper.",
    "Clay sculptures take shape under careful hands.",
    "Paint palettes hold rainbow color selections.",
    "Easels stand ready for artistic creations.",
    "Sketch pads await creative inspiration quietly.",
    "Craft supplies transform ideas into reality.",
    "Clean paint brushes thoroughly after each use.",
    "Store art supplies in proper containers.",
    "Organize color materials by rainbow order.",
    "Cover work surfaces before starting art projects.",
    "Display finished artwork on classroom walls.",
    "Share art supplies fairly with classmates.",
    "Return materials promptly after completing projects.",
    "Label personal art supplies clearly.",
    "Keep art spaces neat and organized.",
    "Respect other students' artwork carefully.",
    "A journey of a thousand miles begins with a single step.",
    "To be or not to be, that is the question.",
    "All that glitters is not gold.",
    "Where there's a will, there's a way.",
    "The early bird catches the worm.",
    "Actions speak louder than words.",
    "Knowledge is power, but enthusiasm pulls the switch.",
    "Life is what happens when you're busy making other plans.",
    "The only way to do great work is to love what you do.",
    "Every cloud has a silver lining in the sky.",
    "Practice makes perfect in everything you pursue.",
    "Success is falling nine times and getting up ten.",
    "Small progress each day adds up to big results.",
    "Kindness is a language that everyone can understand.",
    "Your attitude determines your direction in life's journey.",
    "Great things take time, so be patient with yourself.",
    "Every expert was once a beginner starting fresh.",
    "Tomorrow is another chance to make things right.",
    "Believe you can and you are halfway there.",
    "One kind word can change someone's entire day.",
    "Hard work beats talent when talent doesn't work hard.",
    "Every accomplishment starts with the decision to try.",
    "The best time to plant a tree was yesterday.",
    "Good things come to those who work hard daily.",
    "Your potential is limited only by your imagination.",
    "Happiness is found when you stop comparing yourself to others.",
    "Success is built on daily habits, not occasional events.",
    "Every mistake is a chance to learn something new.",
    "Small steps every day lead to great achievements.",
    "Courage is being scared but doing it anyway.",
    "Dreams don't work unless you take action today.",
    "Your future depends on what you do right now.",
    "Positive thoughts create positive things in your life.",
    "The harder you work, the luckier you become.",
    "Every day is a new beginning, take a step.",
    "Little by little, one travels far in life.",
    "Persistence can change failure into extraordinary achievement.",
    "The best view comes after the hardest climb.",
    "Fall seven times, stand up eight times stronger.",
    "A goal without a plan is just a wish.",
    "Today's preparation determines tomorrow's achievement.",
    "Difficult roads often lead to beautiful destinations.",
    "Every moment is a fresh beginning for change.",
    "Your time is limited, so make it count today.",
    "Be the change you wish to see around you.",
    "Success comes from learning from your mistakes daily.",
    "What lies behind you pales to what lies ahead.",
    "The secret of getting ahead is getting started now.",
    "Opportunities multiply as they are seized with courage.",
    "Make each day your masterpiece of living."
    ];
    setCurrentSentence(sentences[Math.floor(Math.random() * sentences.length)]);
  };

  useEffect(() => {
    const fetchNewContent = () => {
      if (currentImageIndex === -1 && isFirstImageFetched) {
        // Switch from placeholder to first fetched image
        addLogMessage('Switching from placeholder to first fetched image');
        setCurrentImageIndex(0);
        setCurrentImage(imageCache[0]);
        setUsedImages(prev => new Set(prev).add(imageCache[0]));
        fetchRandomSentence();
      } else if (imageCache.length > 0) {
        const nextIndex = (currentImageIndex + 1) % imageCache.length;
        addLogMessage(`Updating to next image at index: ${nextIndex}`);
        setCurrentImageIndex(nextIndex);
        setCurrentImage(imageCache[nextIndex]);
        setUsedImages(prev => new Set(prev).add(imageCache[nextIndex]));
        fetchRandomSentence();
      } else {
        addLogMessage('Cache is empty, cannot update image');
      }
      updateImageCache();
    };

    const contentInterval = setInterval(fetchNewContent, IMAGE_DISPLAY_DURATION);
    return () => clearInterval(contentInterval);
  }, [imageCache, currentImageIndex, isFirstImageFetched, usedImages]);

  const handleClose = () => {
    navigate('/');
  };

  const handleEdit = () => {
    navigate('/RhythmChoosing', { 
      state: { 
        returnTo: '/exercise4workout',
        duration: initialDuration,  // Pass the duration in minutes, not seconds
        time: time
      } 
    });
  };

  // Update breathing animation effect
  useEffect(() => {
    let timeout;
    if (!isPlaying) {
      const steps = [
        [stick1, stick1], // Pair of sticks for Step 1
        [stick2, stick2], // Pair of sticks for Step 2
        [stick3, stick3],
        [stick4, stick4],
        [stick5, stick5],
        [stick6, stick6],
        [stick7, stick7],
      ];
      const durations = [long, long, long, short, short, short, short];
      
      const fadeInNextCircle = () => {
        timeout = setTimeout(() => {
          setCurrentStep(nextStep);
          
          if (currentStep == 2 || currentStep == 3) {
            setInstructionText('Inhale');
          } else if (currentStep == 4) {
            setInstructionText('Hold');
          } else if (currentStep >= 5 && currentStep <= 7) {
            setInstructionText('Exhale');
          } else {
            setInstructionText('Hold');
          }

          if (nextStep === 1) {
            setIsTransitioningToOne(true);
            setFadeInStyle({ opacity: 1, transition: `opacity 0.2s ease-in` });
            setFadeOutStyle({ opacity: 0, transition: `opacity 0.2s ease-out` });
          } else {
            setIsTransitioningToOne(false);
            setFadeInStyle({ opacity: 1, transition: `opacity ${durations[nextStep - 1]}s ease-in` });
          }

          timeout = setTimeout(() => {
            setPreviousStep(null);
            timeout = setTimeout(() => {
              setNextStep((prev) => (prev === 7 ? 1 : prev + 1));
              setPreviousStep(currentStep);
              fadeInNextCircle();
            }, hold * 1000);
          }, nextStep === 1 ? 200 : durations[nextStep - 1] * 1000);
        }, 10);
      };

      fadeInNextCircle();
    }

    return () => clearTimeout(timeout);
  }, [isPlaying, nextStep, long, short, hold, currentStep]);

// Spacebar feature
  useEffect(() => {
      const handleKeyDown = (event) => {
        if (event.code === "Space") {
          setIsPressed(true);
          setErrorRate(prevError => prevError + 1);


        }
      };

      const handleKeyUp = (event) => {
        if (event.code === "Space") {
          setIsPressed(false);
        }
      };

      window.addEventListener("keydown", handleKeyDown);
      window.addEventListener("keyup", handleKeyUp);

      return () => {
        window.removeEventListener("keydown", handleKeyDown);
        window.removeEventListener("keyup", handleKeyUp);
      };
    }, []);


  return (
    <div className="exercise-container">
      {showCurtain && <Curtain appear={true} duration={2000} />}
      <div className="header-bar">
        <div className="close-icon-wrapper">
          <img src={CloseIcon} alt="close icon" className="close-icon" onClick={handleClose} />
        </div>
        <div className="progress-bar-container">
          <div className="progress-bar">
            <div className="progress" style={{ width: `${progress}%` }}></div>
          </div>
          <span>{`${Math.floor(time / 60)}:${String(Math.floor(time % 60)).padStart(2, '0')}`}</span>
        </div>

        <div className="button-container">
          <button className="save-button" onClick={handleEdit}>Edit Rhythm</button>
        </div>
      </div>

      <div className="exercise4-main-content">
        <div className="image-container">
          {currentImage && <img src={currentImage} alt="Random content" className="exercise-image" />}
        </div>
        <div className="sentence-container">
          <p>{currentSentence}</p>
        </div>
        {[
              [stick1, stick1],
              [stick2, stick2],
              [stick3, stick3],
              [stick4, stick4],
              [stick5, stick5],
              [stick6, stick6],
              [stick7, stick7],
            ].map((StickPair, index) => (
              <div key={index} className="stick-container4">
                {/* Left Stick */}
              <img
                src={StickPair[0]}
                alt={`stick left step ${index + 1}`}
                className="breathing4 l-stick"
                style={
                  currentStep === index + 1
                    ? fadeInStyle
                    : previousStep === index + 1 ||
                      (index === 0 && currentStep === 2)
                    ? index === 6 && isTransitioningToOne
                      ? fadeOutStyle
                      : { opacity: 1 }
                    : { opacity: 0 }
                }
              />
                {/* Right Stick */}
              <img
                src={StickPair[1]}
                alt={`stick right step ${index + 1}`}
                className="breathing4 r-stick"
                style={
                  currentStep === index + 1
                    ? fadeInStyle
                    : previousStep === index + 1 ||
                      (index === 0 && currentStep === 2)
                    ? index === 6 && isTransitioningToOne
                      ? fadeOutStyle
                      : { opacity: 1 }
                    : { opacity: 0 }
                }
              />
              </div>
            ))}

      </div>
      <div className={`spacebar-btn ${isPressed ? "pressed" : ""}`}>
                      SPACE
                    </div>
            <div className="instruction-box">
                    <img src={InfoIcon} alt="info icon" className="instruction-icon" />
                    <ul>

                        <li>Press the spacebar whenever you feel that you haven't comprehended the sentence</li>
                      </ul>
                  </div>


      {/* <div className="instruction-box">
        <img src={InfoIcon} alt="info icon" className="instruction-icon" />
        Comprehend the sentence and the picture at the same time. The picture is not related to the text at the bottom.
      </div> */}

      {/* <div className="log-container" ref={logContainerRef}>
        {logMessages.map((message, index) => (
          <div key={index} className="log-message">{message}</div>
        ))}
      </div> */}
    </div>
  );
}

export default Exercise4Workout;